import type * as zod from 'zod';

import { EventEmitter, wait } from '@sb/utilities';

import { FailureKind } from '../../FailureKind';
import type { Conditional, Expression, StepFailure } from '../../types';
import type { StepPlayArguments } from '../Step';
import Step from '../Step';

import Arguments from './Arguments';
import Variables from './Variables';

type Arguments =
  | {
      milliseconds: number;
    }
  | {
      expression: Expression;
    }
  | {
      condition: Conditional;
    };

type Variables = zod.infer<typeof Variables>;

const TIMEOUT_ERROR_RATIO: number = 1.05;
const TIMEOUT_WARNING_RATIO: number = 1.025;

export default class WaitStep extends Step<Arguments, Variables> {
  private events = new EventEmitter<{
    stop: void;
    complete: void;
    failure: StepFailure;
  }>();

  public static areSubstepsRequired = false;

  public static Arguments = Arguments;

  public static Variables = Variables;

  protected initializeVariableState(): void {
    this.variables = {};
  }

  /**
   * @internal
   */
  private timeout: ReturnType<typeof setTimeout> | null = null;

  /**
   * Used for pause/resume handling
   * @internal
   */
  private lastStartTime = new Date();

  /**
   * Used for pause/resume handling
   * @internal
   */
  private elapsedMilliseconds = 0;

  private paused = false;

  /**
   * Stores the resolved wait time in ms. For literal args this equals
   * `this.args.milliseconds`; for expression args it is computed once
   * during `_play()`.
   */
  private resolvedMilliseconds: number | undefined;

  public async _play({ fail }: StepPlayArguments): Promise<void> {
    this.elapsedMilliseconds = 0;

    if ('expression' in this.args) {
      const evaluated = await this.routineContext.evaluateExpression(
        this.args.expression,
      );

      if (typeof evaluated !== 'number') {
        await fail({
          failure: {
            kind: FailureKind.InvalidRoutineLoadedFailure,
          },
          failureReason: `Wait time expression must evaluate to a number, but got ${typeof evaluated}`,
        });

        return;
      }

      if (!Number.isFinite(evaluated)) {
        await fail({
          failure: {
            kind: FailureKind.InvalidRoutineLoadedFailure,
          },
          failureReason: `Wait time must be a finite number, but got ${evaluated}`,
        });

        return;
      }

      if (evaluated < 0) {
        await fail({
          failure: {
            kind: FailureKind.InvalidRoutineLoadedFailure,
          },
          failureReason: `Wait time must be non-negative, but got ${evaluated}`,
        });

        return;
      }

      this.resolvedMilliseconds = Math.round(evaluated * 1000);
    } else if ('milliseconds' in this.args) {
      this.resolvedMilliseconds = this.args.milliseconds;
    }

    const offFailure = this.events.on('failure', (failure) => fail(failure));

    const promise = this.events.race('failure', 'complete');

    // If the step was paused during the (awaited) expression evaluation, do
    // not start the timer here — the framework will call _resume() when the
    // user resumes, which will start the timer with the resolved value.
    if (!this.paused) {
      this._resume();
    }

    this._evaluateConditionLoop();

    await promise;

    offFailure();
  }

  public _pause(): void {
    this.paused = true;

    if (this.timeout !== null) {
      clearTimeout(this.timeout);

      const elapsedSinceStart =
        new Date().getTime() - this.lastStartTime.getTime();

      this.elapsedMilliseconds += elapsedSinceStart;
    }
  }

  public _resume(): void {
    this.paused = false;

    this.lastStartTime = new Date();
    let stopped = false;

    this.events.once('stop', () => {
      stopped = true;
    });

    // reset/start timer
    if (this.resolvedMilliseconds != null) {
      this.timeout = setTimeout(() => {
        if (stopped) {
          return;
        }

        this.events.emit('complete');
      }, this.resolvedMilliseconds - this.elapsedMilliseconds);
    }
  }

  public _stop() {
    this.paused = false;

    if (this.timeout !== null) {
      clearTimeout(this.timeout);
    }

    this.events.emit('stop');
  }

  public hasSelfTimeout(): boolean {
    if (this.getWaitTime()) {
      return true;
    }

    return false;
  }

  public getSelfTimeoutWarning(): number {
    const waitTime = this.getWaitTime();

    return waitTime ? waitTime * TIMEOUT_WARNING_RATIO : 0;
  }

  public getSelfTimeout(): number {
    const waitTime = this.getWaitTime();

    return waitTime ? waitTime * TIMEOUT_ERROR_RATIO : 0;
  }

  private getWaitTime(): number | undefined {
    // After _play(), use the resolved value (covers both literal and expression)
    if (this.resolvedMilliseconds != null) {
      return this.resolvedMilliseconds;
    }

    // Before _play(), return the literal value if available (for pre-play timeout setup)
    if ('milliseconds' in this.args) {
      return this.args.milliseconds;
    }

    return undefined;
  }

  private _evaluateConditionLoop(): void {
    // time-based wait steps are handled separately so don't worry about it here
    if (!('condition' in this.args)) {
      return;
    }

    const { condition } = this.args;

    let stopped = false;

    this.events.once('stop', () => {
      stopped = true;
    });

    (async () => {
      while (!stopped) {
        // make sure we are not still evaluating expressions when paused
        if (this.paused) {
          await wait(100);
          continue;
        }

        try {
          if (await this.routineContext.evaluateConditional(condition)) {
            this.events.emit('complete');

            return;
          }

          await wait(100);
        } catch (error) {
          this.events.emit('failure', {
            failure: {
              kind: FailureKind.InvalidRoutineLoadedFailure,
            },
            failureReason: error.message,
            error,
          });

          break;
        }
      }
    })();
  }
}
